'use client'

import { type PopupProps, type SelectValue } from '@mui/base'
import { unstable_useControlled as useControlled } from '@mui/utils'
import {
  type FocusEvent,
  type JSX,
  type KeyboardEvent,
  type MouseEvent,
  useCallback,
  useEffect,
} from 'react'

import { Select, type SelectProps } from 'base-ui'

import { useProductSearchRefinementStateContext } from '../ProductSearchRefinementStateContext'

import { ProductSearchRefinementPopup } from './ProductSearchRefinementPopup'
import { ProductSearchRefinementRoot } from './ProductSearchRefinementRoot'

const popupProps = {
  disablePortal: true,

  /**
   * Used for performance optimization, to prevent unnecessary
   * `Select` items rendering if the `Select` is closed.
   * Read additional details in the `onChange` function.
   */
  keepMounted: false,

  middleware: [],
  placement: 'bottom-start',
} satisfies PopupProps

const useProductSearchRefinementControls = (
  attribute: string,
  listboxOpen?: boolean,
): {
  open: boolean
  setOpen: (open: boolean) => void
} => {
  const {
    dispatch,
    state: { openedSelectId },
  } = useProductSearchRefinementStateContext()

  const [value, setValue] = useControlled({
    controlled: listboxOpen,
    default: false,
    name: 'ProductSearchRefinement',
    state: 'listboxOpen',
  })

  const setOpen = useCallback(
    (open: boolean) => {
      if (open) {
        dispatch({
          payload: attribute,
          type: 'closeOtherFilters',
        })
      }

      setValue(open)
    },
    [attribute, dispatch, setValue],
  )

  useEffect(() => {
    if (value && openedSelectId && openedSelectId !== attribute) {
      setValue(false)
    }
  }, [attribute, openedSelectId, setValue, value])

  return {
    open: value,
    setOpen,
  }
}

export type ProductSearchRefinementProps<
  Value extends NonNullable<unknown>,
  Multiple extends boolean = false,
> = Omit<SelectProps<Value, Multiple>, 'slotProps'> & {
  attribute: string
  slotProps?: Omit<
    Exclude<SelectProps<Value, Multiple>['slotProps'], undefined>,
    'popup'
  > & {
    popup?: PopupProps
  }
}

/**
 * Basic component for building search filters with additional subcomponents in this folder.
 */
export const ProductSearchRefinement = <
  Value extends NonNullable<unknown>,
  Multiple extends boolean = false,
>({
  attribute,
  children,
  listboxOpen,
  onChange: onChangeProp,
  slotProps,
  slots,
  ...selectProps
}: ProductSearchRefinementProps<Value, Multiple>): JSX.Element => {
  const {
    state: { isMobile },
  } = useProductSearchRefinementStateContext()

  const { open, setOpen } = useProductSearchRefinementControls(
    attribute,
    listboxOpen,
  )

  const onChange = useCallback(
    (
      event: FocusEvent | KeyboardEvent | MouseEvent | null,
      value: SelectValue<Value, Multiple>,
    ): void => {
      /**
       * `null` is happening when `Select` `Option`s are changing dynamically.
       * In the current case it is triggered when `Popup` unmounts.
       * See `slotProps.popup.keepMounted` value.
       */
      if (event === null) {
        return
      }

      onChangeProp?.(event, value)
    },
    [onChangeProp],
  )

  const onClick = useCallback(() => {
    if (!isMobile) {
      setOpen(!open)
    }
  }, [isMobile, open, setOpen])

  return (
    <div>
      <Select
        listboxOpen={open}
        name={attribute}
        onChange={onChange}
        onClick={onClick}
        onListboxOpenChange={isMobile ? setOpen : undefined}
        slotProps={{
          ...slotProps,
          listbox: {
            allowTabNavigation: true,
            ...slotProps?.listbox,
          },
          popup: {
            ...popupProps,
            ...slotProps?.popup,
          },
        }}
        slots={{
          popup: ProductSearchRefinementPopup,
          root: ProductSearchRefinementRoot,
          ...slots,
        }}
        {...selectProps}
      >
        {children}
      </Select>
    </div>
  )
}
